import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import baseUrl from "../../../../api/baseUrl";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import PersonIcon from "@mui/icons-material/Person";
import PrintIcon from "@mui/icons-material/Print";
import EditIcon from "@mui/icons-material/Edit";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import socket from "../../../../api/socket";
import AppointmentDetailsModal from "./AppointmentDetailsModal";
import {
  APPOINTMENT_STATUS_OPTIONS,
  bookingSourceStyles,
  doctorNameFromItem,
  formatAppointmentPurpose,
  formatPreAppointmentStatus,
  isAppointmentCancelled,
  isRoomAllocationEnabled,
  latestTransferDoctorNameFromItem,
  normalizeLifecycleStatus,
  patientMobileFromItem,
  patientRegNoFromItem,
  patientTypeLabelFromItem,
  PATIENT_TYPE_STYLES,
  formatPatientTypeLabel,
  preAppointmentStatusFromItem,
  purposeColorBackground,
  purposeColorFromItem,
  transferDoctorNamesFromItem,
  statusStyles,
} from "./appointmentViewShared";
import { isDoctorTransferEnabled } from "../../../../utils/clinicFeatures";
import PatientViewModal from "../../../patient/components/PatientViewModal";
import TokenPrint from "../../../setting/manageprint/tokenprint/TokenPrint";
import {
  appointmentHasToken,
  dispatchTokenPrintLayout,
  doctorNameFromAppointment,
  patientNameFromAppointment,
  roomNumberFromAppointment,
} from "../../../../utils/dispatchTokenPrint";
import { printElementToPdf } from "../../../../utils/printElement";
import { TOKEN_PAPER_WIDTH_MM } from "../../../../utils/tokenPrintPaper";
import {
  openPatientSummaryFromAppointment,
  tokenInfoFromAppointment,
} from "../../../../utils/openPatientSummaryInNewTab";
import { formatTokenTime } from "../../../patient/components/ModalHeader";
import { useOngoingStatusGate } from "./useOngoingStatusGate";
import UpdateTokenModal from "./UpdateTokenModal";
import GenerateTokenConfirmModal from "./GenerateTokenConfirmModal";

const DENTIST_STATUS_BUTTONS = [
  { value: "ongoing", label: "Ongoing" },
  { value: "in_queue", label: "In Queue" },
  { value: "complete", label: "Complete" },
] as const;

function AppointmentTr({
  item,
  appointmStatus,
  showAddTreatmentButton = true,
  showPatientDetailsButton = false,
  showViewAppointmentButton = true,
  showPrintTokenButton = true,
  showDoctorColumn = true,
  statusAsButtons = false,
  hideCancelAction = false,
  showQueueActions = isRoomAllocationEnabled(),
  tokenOnly = false,
  myAppointmentLayout = false,
  onOngoingStatus,
}: any) {
  const roomAllocationEnabled = isRoomAllocationEnabled();
  const doctorTransferEnabled = isDoctorTransferEnabled();
  const dispatch = useDispatch();
  const pendingTokenPrint = useRef(false);
  const [printSession, setPrintSession] = useState(0);
  const [select, setselect] = useState(
    () => normalizeLifecycleStatus(item?.status) || item?.status || ""
  );
  const [printingToken, setPrintingToken] = useState(false);
  const hasToken = appointmentHasToken(item?.queue);
  const tokenGeneratedTime =
    roomAllocationEnabled && hasToken
      ? formatTokenTime(item?.queueGeneratedAt)
      : "";
  const patientRegNo = patientRegNoFromItem(item);
  const patientPhone = patientMobileFromItem(item);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const navigate = useNavigate();
  const [patientModalOpen, setPatientModalOpen] = useState(false);
  const [patientLoading, setPatientLoading] = useState(false);
  const [patientData, setPatientData] = useState<any>(null);
  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [tokenSaving, setTokenSaving] = useState(false);
  const [generateTokenModalOpen, setGenerateTokenModalOpen] = useState(false);
  const [generatingToken, setGeneratingToken] = useState(false);
  const pendingStatusRef = useRef<string | null>(null);
  const [theme, setTheme] = useState<"light" | "dark">("light");
  const { gateStatus, ongoingGateModal } = useOngoingStatusGate();

  useEffect(() => {
    if (pendingStatusRef.current) return;
    setselect(normalizeLifecycleStatus(item?.status) || item?.status || "");
  }, [item?.status, item?._id]);

  useEffect(() => {
    const htmlTheme = document.documentElement.getAttribute("data-theme") as "light" | "dark";
    const savedTheme = (localStorage.getItem("theme") as "light" | "dark") || htmlTheme;
    if (savedTheme) setTheme(savedTheme);
  }, []);

  const commitStatus = useCallback(
    (status: string) => {
      if (!status) return;

      pendingStatusRef.current = status;
      setselect(status);

      socket.emit("allAppointmentStatus", {
        appointmentid: item?._id,
        status,
        queue: item?.queue,
        roomNumber: item?.roomNumber,
        sessionName: item?.sessionName,
      });
      if (
        roomAllocationEnabled &&
        status === "ongoing" &&
        item?.queue != null &&
        item?.queue !== ""
      ) {
        try {
          localStorage.setItem(
            "nextQueue",
            String(Math.max(0, Math.floor(Number(item.queue))))
          );
        } catch {
          /* ignore quota / private mode */
        }
      }

      if (status === "ongoing") {
        openPatientSummaryFromAppointment(item);
      }

      baseUrl
        .patch(
          "/appointment/statusAppointmentById",
          { status },
          {
            headers: { appointmentid: item?._id, status },
          }
        )
        .then(() => {
          pendingStatusRef.current = null;
          appointmStatus();
        })
        .catch((error) => {
          pendingStatusRef.current = null;
          setselect(normalizeLifecycleStatus(item?.status) || item?.status || "");
          console.log(error);
          const msg =
            error?.response?.data?.error ||
            error?.message ||
            "Could not update appointment status.";
          toast.error(msg);
        });
    },
    [appointmStatus, item, roomAllocationEnabled]
  );

  const applyStatus = useCallback(
    async (status: string) => {
      if (!status) return;
      const next = normalizeLifecycleStatus(status) || status;
      const current = normalizeLifecycleStatus(select) || select;
      if (next === current) return;
      const ok = await gateStatus(item, next);
      if (!ok) return;
      commitStatus(next);
    },
    [commitStatus, gateStatus, item, select]
  );

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    void applyStatus(e.target.value);
  };

  const handleTreatmentClick = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const appointmentId = item?._id ? String(item._id) : undefined;
      baseUrl
        .post("treatment/create-treatment", {
          patientId: id,
          ...(appointmentId ? { appointmentId } : {}),
        })
        .then((response) => {
          const qs = appointmentId
            ? `?appointmentId=${encodeURIComponent(appointmentId)}`
            : "";
          navigate(
            `/client/treatment-diagnose/add-treatment/treatment-table/${response.data.data._id}${qs}`
          );
        })
        .catch((error) => console.error(error));
    },
    [navigate, item?._id]
  );

  const handleDiagnosisClick = useCallback(
    (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const appointmentId = item?._id ? String(item._id) : undefined;
      baseUrl
        .post("treatment/create-treatment", {
          patientId: id,
          ...(appointmentId ? { appointmentId } : {}),
        })
        .then((response) => {
          const qs = appointmentId
            ? `?appointmentId=${encodeURIComponent(appointmentId)}`
            : "";
          navigate(
            `/client/treatment-diagnose/add-treatment/diagnose/${response.data.data._id}${qs}`
          );
        })
        .catch((error) => console.error(error));
    },
    [navigate, item?._id]
  );

  const handleViewPatient = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      const pid = item?.patientId?._id;
      if (!pid) return;
      setPatientModalOpen(true);
      setPatientLoading(true);
      try {
        const res = await baseUrl.get("patient/findById", {
          headers: { patientid: pid },
        });
        setPatientData(res.data?.data ?? null);
      } catch (err) {
        console.error(err);
        setPatientData(null);
      } finally {
        setPatientLoading(false);
      }
    },
    [item?.patientId?._id]
  );

  const closePatientModal = useCallback(() => {
    setPatientModalOpen(false);
    setPatientData(null);
  }, []);

  const canUpdateQueue =
    roomAllocationEnabled &&
    String(item?.sessionName ?? "").trim() !== "" &&
    String(item?.roomNumber ?? "").trim() !== "";

  const openUpdateTokenModal = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!canUpdateQueue || !item?._id || tokenSaving) return;
      setTokenModalOpen(true);
    },
    [canUpdateQueue, item?._id, tokenSaving]
  );

  const handleConfirmUpdateToken = useCallback(
    async (n: number) => {
      if (!canUpdateQueue || !item?._id || tokenSaving) return;

      setTokenSaving(true);

      try {
        await baseUrl.patch(
          "/appointment/queueAppointmentById",
          { queue: n },
          { headers: { appointmentid: item._id } }
        );

        socket.emit("allAppointmentStatus", {
          appointmentid: item._id,
          status: item?.status,
          queue: n,
          roomNumber: item?.roomNumber,
          sessionName: item?.sessionName,
        });

        setTokenModalOpen(false);
        appointmStatus?.();
      } catch (err) {
        console.error(err);
        toast.error("Could not update token. Please try again.");
      } finally {
        setTokenSaving(false);
      }
    },
    [appointmStatus, canUpdateQueue, item, tokenSaving]
  );

  const printTokenHostRef = useCallback(
    async (node: HTMLDivElement | null) => {
      if (!node || !pendingTokenPrint.current) return;
      pendingTokenPrint.current = false;

      try {
        await new Promise((resolve) => setTimeout(resolve, 80));
        await printElementToPdf(node, {
          contentSelector: ".billtext",
          paperWidthMm: TOKEN_PAPER_WIDTH_MM,
        });
      } catch (err) {
        console.error(err);
        toast.error("Could not print token. Please try again.");
      } finally {
        setPrintingToken(false);
      }
    },
    []
  );

  const handlePrintToken = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!hasToken || !roomAllocationEnabled || printingToken) return;

      try {
        const response = await baseUrl.get("/clinic/getBillData");
        const clinic = response.data?.data;

        dispatchTokenPrintLayout(dispatch, clinic, {
          tokenNo: Number(item.queue),
          roomNumber: roomNumberFromAppointment(item),
          doctorName: doctorNameFromAppointment(item),
          patientName: patientNameFromAppointment(item),
          issuedAt: item?.queueGeneratedAt
            ? new Date(item.queueGeneratedAt)
            : new Date(),
        });

        pendingTokenPrint.current = true;
        setPrintSession((n) => n + 1);
        setPrintingToken(true);
      } catch (err) {
        console.error(err);
        toast.error("Could not load token layout.");
        pendingTokenPrint.current = false;
        setPrintingToken(false);
      }
    },
    [dispatch, hasToken, item, printingToken, roomAllocationEnabled]
  );

  const handleGenerateQueue = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!canUpdateQueue || !item?._id || generatingToken) return;
      setGenerateTokenModalOpen(true);
    },
    [canUpdateQueue, item, generatingToken]
  );

  const handleConfirmGenerateToken = useCallback(async () => {
    if (!canUpdateQueue || !item?._id || generatingToken) return;
    setGeneratingToken(true);
    try {
      const res = await baseUrl.patch(
        "/appointment/generateQueueAppointmentById",
        {},
        { headers: { appointmentid: item._id } }
      );
      const q = res?.data?.data?.queue;
      if (q == null) return;

      setselect("inqueue");
      socket.emit("allAppointmentStatus", {
        appointmentid: item._id,
        status: "inqueue",
        queue: q,
        roomNumber: item?.roomNumber,
        sessionName: item?.sessionName,
      });
      appointmStatus?.();
      setGenerateTokenModalOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("Could not generate token. Please try again.");
    } finally {
      setGeneratingToken(false);
    }
  }, [appointmStatus, canUpdateQueue, generatingToken, item]);

  const updateTokenIconButton = (
    <button
      type="button"
      onClick={openUpdateTokenModal}
      className="inline-flex items-center justify-center w-6 h-6 rounded-md text-primary hover:bg-primary/10 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/30"
      title="Update token number"
      aria-label="Update token number"
    >
      <EditIcon sx={{ fontSize: 14 }} />
    </button>
  );

  const generateTokenIconButton = (
    <button
      type="button"
      onClick={handleGenerateQueue}
      className="inline-flex items-center justify-center w-6 h-6 rounded-md text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-900/40 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
      title="Generate next token number"
      aria-label="Generate next token number"
    >
      <AutorenewIcon sx={{ fontSize: 14 }} />
    </button>
  );

  const patientCell = (
    <td className="px-2.5 xl:px-3 py-2 text-xs font-semibold text-slate-900 dark:text-slate-100">
      <div className="truncate" title={patientNameFromAppointment(item)}>
        {patientNameFromAppointment(item)}
      </div>
      {(() => {
        const source = preAppointmentStatusFromItem(item);
        const label = formatPreAppointmentStatus(source);
        if (!label) return null;
        return (
          <span
            className={`mt-1 inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-semibold border ${
              bookingSourceStyles[source] || bookingSourceStyles.default
            }`}
            title={source === "cancel" ? "Cancelled" : `Booked via ${label}`}
          >
            {source === "cancel" ? "Cancelled" : `Booked · ${label}`}
          </span>
        );
      })()}
      {doctorTransferEnabled && latestTransferDoctorNameFromItem(item) ? (
        <div className="mt-0.5 text-[11px] font-normal text-sky-700 dark:text-sky-300">
          Transferred to {latestTransferDoctorNameFromItem(item)}
        </div>
      ) : null}
    </td>
  );

  const regNoCell = (
    <td
      className="px-2.5 xl:px-3 py-2 text-xs tabular-nums text-slate-600 dark:text-slate-300 whitespace-nowrap"
      title={patientRegNo !== "—" ? patientRegNo : undefined}
    >
      {patientRegNo}
    </td>
  );

  const phoneCell = (
    <td
      className="px-2.5 xl:px-3 py-2 text-xs tabular-nums text-slate-600 dark:text-slate-300 whitespace-nowrap"
      title={patientPhone !== "—" ? patientPhone : undefined}
    >
      {patientPhone}
    </td>
  );

  const doctorCell = showDoctorColumn ? (
    <td className="px-2.5 xl:px-3 py-2 text-xs text-slate-700 dark:text-slate-200">
      <div className="truncate" title={doctorNameFromItem(item)}>
        {doctorNameFromItem(item)}
      </div>
    </td>
  ) : null;

  const datetimeCell = (
    <td className="px-2.5 xl:px-3 py-2 whitespace-nowrap">
      <div className="text-xs font-semibold text-slate-800 dark:text-slate-100">
        {item?.date?.split("T")[0]}
      </div>
      <div className="mt-0.5 text-[11px] text-slate-600 dark:text-slate-300">
        {item?.startTime} - {item?.endTime}
      </div>
    </td>
  );

  const tokenNumberCell = (
    <td className="px-2.5 xl:px-3 py-2 whitespace-nowrap text-center">
      <div className="text-3xl font-bold tabular-nums text-slate-900 dark:text-slate-50 leading-none tracking-tight">
        {item?.queue != null && item?.queue !== "" ? Number(item.queue) : "—"}
      </div>
      {myAppointmentLayout && showQueueActions && canUpdateQueue ? (
        <div className="mt-1.5 flex items-center justify-center gap-1">
          {updateTokenIconButton}
          {generateTokenIconButton}
        </div>
      ) : null}
      {tokenGeneratedTime ? (
        <div
          className="mt-1 text-[11px] font-medium text-slate-600 dark:text-slate-300"
          title={`Token generated at ${tokenGeneratedTime}`}
        >
          {tokenGeneratedTime}
        </div>
      ) : null}
    </td>
  );

  const locationCell = (
    <td className="px-2.5 xl:px-3 py-2 whitespace-nowrap">
      {tokenOnly ? (
        <>
          <div className="text-3xl font-bold tabular-nums text-slate-900 dark:text-slate-50 leading-none tracking-tight">
            {item?.queue != null && item?.queue !== ""
              ? Number(item.queue)
              : "—"}
          </div>
          {showQueueActions && canUpdateQueue ? (
            <div className="mt-1.5 flex items-center gap-1">
              {updateTokenIconButton}
              {generateTokenIconButton}
            </div>
          ) : null}
          {tokenGeneratedTime ? (
            <div
              className="mt-1 text-[11px] font-medium text-slate-600 dark:text-slate-300"
              title={`Token generated at ${tokenGeneratedTime}`}
            >
              Generated {tokenGeneratedTime}
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className="text-xs font-medium text-slate-800 dark:text-slate-100">
            {item?.sessionName || "—"}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-600 dark:text-slate-300">
            <span>Room {String(item?.roomNumber ?? "").trim() || "—"}</span>
            {showQueueActions && canUpdateQueue ? (
              <div className="flex items-center gap-1">
                {updateTokenIconButton}
                {generateTokenIconButton}
              </div>
            ) : null}
          </div>
        </>
      )}
    </td>
  );

  const patientTypeLabel = patientTypeLabelFromItem(item);
  const patientTypeCell = (
    <td className="px-2.5 xl:px-3 py-2 whitespace-nowrap">
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-lg text-[11px] font-semibold border ${
          PATIENT_TYPE_STYLES[patientTypeLabel] ||
          PATIENT_TYPE_STYLES["Existing Patient"]
        }`}
      >
        {formatPatientTypeLabel(patientTypeLabel)}
      </span>
    </td>
  );

  const statusCell = (
    <td className="px-2.5 xl:px-3 py-2">
      {isAppointmentCancelled(item) ? (
        <span className="text-xs text-slate-400 dark:text-slate-500">—</span>
      ) : statusAsButtons ? (
        <div className="flex flex-wrap gap-1.5">
          {DENTIST_STATUS_BUTTONS.map(({ value, label }) => {
            const active =
              normalizeLifecycleStatus(select) === value || select === value;
            return (
              <button
                key={value}
                type="button"
                onClick={() => void applyStatus(value)}
                className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 ${
                  active
                    ? statusStyles[value] || statusStyles.default
                    : "bg-white dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600 hover:border-slate-300 dark:hover:border-slate-500"
                }`}
                aria-pressed={active}
              >
                {label}
              </button>
            );
          })}
          {!hideCancelAction ? (
            <button
              type="button"
              onClick={() => void applyStatus("cancel")}
              className="px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-colors focus:outline-none focus:ring-2 focus:ring-red-500/20 bg-white dark:bg-slate-700 text-red-600 dark:text-red-300 border-red-200 dark:border-red-700 hover:border-red-400"
            >
              Cancel
            </button>
          ) : null}
        </div>
      ) : (
        <select
          onChange={handleChange}
          value={normalizeLifecycleStatus(select) || select || ""}
          className={`px-2.5 py-1 rounded-lg text-xs font-medium border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors ${
            statusStyles[normalizeLifecycleStatus(select) || select] ||
            statusStyles.default
          }`}
        >
          <option value="">Select</option>
          {APPOINTMENT_STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
          {!hideCancelAction ? <option value="cancel">Cancel</option> : null}
        </select>
      )}
    </td>
  );

  const purposeCell = (
    <td
      className="px-2.5 xl:px-3 py-2 text-xs text-slate-700 dark:text-slate-200"
      title={formatAppointmentPurpose(item) || undefined}
    >
      <div className="truncate">{formatAppointmentPurpose(item)}</div>
    </td>
  );

  const actionCell = (
    <td className="px-2.5 xl:px-3 py-2">
      <div className="flex gap-2 justify-center items-center">
        {showViewAppointmentButton ? (
          <button
            type="button"
            onClick={() => setDetailsOpen(true)}
            className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 flex items-center justify-center hover:bg-indigo-100 dark:hover:bg-indigo-900/60 active:bg-indigo-200 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-1"
            title="View appointment details"
            aria-label="View appointment details"
          >
            <RemoveRedEyeIcon sx={{ fontSize: 18 }} />
          </button>
        ) : null}
        {showPatientDetailsButton && item?.patientId?._id ? (
          <button
            type="button"
            onClick={handleViewPatient}
            className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-900/40 text-slate-700 dark:text-slate-200 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-900/60 active:bg-slate-200 transition-all focus:outline-none focus:ring-2 focus:ring-slate-500 focus:ring-offset-1"
            title="View patient"
            aria-label="View patient"
          >
            <PersonIcon sx={{ fontSize: 18 }} />
          </button>
        ) : null}
        {showPrintTokenButton && roomAllocationEnabled ? (
          <button
            type="button"
            onClick={handlePrintToken}
            disabled={!hasToken || printingToken}
            className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all focus:outline-none focus:ring-2 focus:ring-offset-1 ${
              hasToken && !printingToken
                ? "bg-emerald-50 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-100 dark:hover:bg-emerald-900/60 focus:ring-emerald-500"
                : "bg-slate-100 dark:bg-slate-800 text-slate-300 dark:text-slate-600 cursor-not-allowed"
            }`}
            title={
              hasToken
                ? printingToken
                  ? "Printing token…"
                  : "Print token"
                : "Generate a token first"
            }
            aria-label="Print token"
          >
            <PrintIcon sx={{ fontSize: 18 }} />
          </button>
        ) : null}
      </div>
      {showPrintTokenButton &&
        printingToken &&
        createPortal(
          <div
            key={printSession}
            ref={printTokenHostRef}
            style={{
              position: "fixed",
              left: "-10000px",
              top: 0,
              zIndex: -1,
              pointerEvents: "none",
            }}
            aria-hidden
          >
            <TokenPrint />
          </div>,
          document.body
        )}
      {showViewAppointmentButton ? (
        <AppointmentDetailsModal
          item={item}
          open={detailsOpen}
          onClose={() => setDetailsOpen(false)}
          onStatusChange={appointmStatus}
          onOngoingStatus={onOngoingStatus}
        />
      ) : null}
      <PatientViewModal
        isOpen={patientModalOpen}
        onClose={closePatientModal}
        patientData={patientData}
        loading={patientLoading}
        theme={theme}
        appointmentId={item?._id}
        transferDoctorNames={transferDoctorNamesFromItem(item) || undefined}
        onTransferSuccess={appointmStatus}
        tokenInfo={tokenInfoFromAppointment(item)}
        onAddTreatment={
          item?.patientId?._id ? handleTreatmentClick : undefined
        }
        onAddDiagnosis={
          item?.patientId?._id ? handleDiagnosisClick : undefined
        }
      />
      {ongoingGateModal}
      <UpdateTokenModal
        open={tokenModalOpen}
        currentToken={item?.queue}
        patientName={patientNameFromAppointment(item)}
        saving={tokenSaving}
        onCancel={() => {
          if (!tokenSaving) setTokenModalOpen(false);
        }}
        onConfirm={handleConfirmUpdateToken}
      />
      <GenerateTokenConfirmModal
        open={generateTokenModalOpen}
        patientName={patientNameFromAppointment(item)}
        saving={generatingToken}
        onCancel={() => {
          if (!generatingToken) setGenerateTokenModalOpen(false);
        }}
        onConfirm={handleConfirmGenerateToken}
      />
    </td>
  );

  const purposeColor = purposeColorFromItem(item);

  return (
    <tr
      className={`transition-colors group ${
        purposeColor
          ? "hover:brightness-[0.97] dark:hover:brightness-110"
          : "hover:bg-indigo-50/40 dark:hover:bg-indigo-950/20"
      }`}
      style={
        purposeColor
          ? { backgroundColor: purposeColorBackground(purposeColor) }
          : undefined
      }
    >
      {myAppointmentLayout ? (
        <>
          {datetimeCell}
          {tokenNumberCell}
          {regNoCell}
          {patientCell}
          {phoneCell}
          {purposeCell}
          {patientTypeCell}
          {statusCell}
          {actionCell}
        </>
      ) : (
        <>
          {datetimeCell}
          {tokenNumberCell}
          {regNoCell}
          {patientCell}
          {phoneCell}
          {purposeCell}
          {doctorCell}
          {locationCell}
          {statusCell}
          {actionCell}
        </>
      )}
    </tr>
  );
}

export default AppointmentTr;
